import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import store from '@/store';
import membersLogsService from '@/services/membersLogs.service';

const HUB_EVENT = 'MemberEntered';
const HUB_PATH = '/hubs/member-entry';
const BACKFILL_LIMIT = 100;

const resolveHubBaseUrl = () => {
  // Dev: go through the dev-server proxy, which forwards /hubs with ws:true and presents the
  // tenant host as Origin (vue.config.js). Connecting direct instead is cross-origin from
  // localhost, and the API only allows https://*.fit-network.com — so it could never negotiate,
  // which is why the popup was untestable outside a deployed environment. A proxy that drops an
  // idle socket is survivable: withAutomaticReconnect reopens it and onreconnected backfills.
  if (process.env.NODE_ENV !== 'production') {
    return '';
  }
  const apiURL = (store.state && store.state.settings && store.state.settings.apiURL) || '';
  return apiURL.replace(/\/api\/?$/, '');
};

// Token is added by accessTokenFactory; duplicating it corrupts the URL.
const buildHubUrl = (branchIds) => `${resolveHubBaseUrl()}${HUB_PATH}?branchId=${encodeURIComponent(branchIds.join(','))}`;

// A key for "which branches is this connection listening on", so a change of selection is a
// reconnect and an unchanged one is a no-op.
const branchKey = (branchIds) => (branchIds || []).map(String).sort().join(',');

let connection = null;
let currentBranchIds = [];
// Serialise concurrent connect/disconnect; the token + branchId watchers race otherwise.
let inflight = Promise.resolve();

const getToken = () => (
  store.state && store.state.auth && store.state.auth.userData
    ? store.state.auth.userData.token
    : ''
);

const runBackfill = async (source, branchIds) => {
  try {
    const since = store.state.popups.memberEntryLastSeenAt;
    // One call per branch: the endpoint answers for a single branch, and the popup queue is a
    // merged stream anyway.
    const params = { limit: BACKFILL_LIMIT };
    if (since) {
      params.since = since;
    }
    // Only real ids: the endpoint answers for one branch and rejects anything else outright,
    // which surfaced as a "branchId is required" toast on the all-branches view.
    const numeric = (branchIds || []).filter((id) => /^\d+$/.test(String(id)));
    const perBranch = await Promise.all(
      numeric.map((branchId) => membersLogsService
        .getRecent({ ...params, branchId })
        // The REST body carries branchId as a JSON number, which a long id outgrows. Each row is
        // from the branch asked for, so that exact id decides whether it stays queued.
        .then((rows) => (Array.isArray(rows) ? rows : [])
          .map((dto) => (dto ? { ...dto, branchId: String(branchId) } : dto)))
        .catch(() => [])),
    );
    // A reconnect's backfill is not awaited, so the desk may have moved on by the time it answers.
    if (connection !== source) return;
    perBranch.flat().forEach((dto) => {
      if (dto) store.commit('enqueueMemberEntry', dto);
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[MemberEntryHub] backfill failed:', err && err.message);
  }
};

// Bound to one connection: a message still in flight from a replaced one names a branch the desk
// has stopped watching.
const handleIncomingEntry = (source) => (dto) => {
  if (!dto || connection !== source) return;
  // Branch isolation is enforced server-side; we don't compare here because
  // long ids exceed Number.MAX_SAFE_INTEGER and would fail === silently.
  store.commit('enqueueMemberEntry', dto);
};

export const isConnected = () => connection
  && connection.state === HubConnectionState.Connected;

const teardown = async () => {
  if (!connection) {
    return;
  }
  const old = connection;
  connection = null;
  currentBranchIds = [];
  try {
    await old.stop();
  } catch (e) { /* ignore */ }
};

const performConnect = async (branchIds) => {
  if (!branchIds || !branchIds.length || !getToken()) {
    return;
  }
  // Already connected (or connecting) to the same branches.
  if (connection && branchKey(currentBranchIds) === branchKey(branchIds)) {
    return;
  }
  // Different selection: tear down before opening a new one.
  if (connection) {
    await teardown();
  }
  // Here rather than when the selection changes: connects run one after another, so the previous
  // one's backfill can still be landing popups for the branch being left until this point.
  store.commit('retainWatchedMemberEntries', branchIds);

  currentBranchIds = branchIds.slice();

  const c = new HubConnectionBuilder()
    .withUrl(buildHubUrl(branchIds), {
      // Wildcard CORS rejects credentials; we use the token in URL/header instead.
      withCredentials: false,
      accessTokenFactory: getToken,
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();

  c.on(HUB_EVENT, handleIncomingEntry(c));
  c.onreconnected(() => {
    if (connection === c && currentBranchIds.length) {
      runBackfill(c, currentBranchIds);
    }
  });
  c.onclose((err) => {
    // eslint-disable-next-line no-console
    console.warn('[MemberEntryHub] connection closed', err && err.message);
  });

  // Assign before start() so concurrent callers short-circuit.
  connection = c;

  try {
    await c.start();
    await runBackfill(c, branchIds);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[MemberEntryHub] failed to start', err && err.message);
    if (connection === c) {
      connection = null;
      currentBranchIds = [];
    }
  }
};

export const connect = (branchIds) => {
  inflight = inflight.then(() => performConnect(branchIds)).catch(() => {});
  return inflight;
};

export const disconnect = () => {
  inflight = inflight.then(() => teardown()).catch(() => {});
  return inflight;
};
